/* DO NOT FORMAT OR CHANGE IMPORT ORDER */
import { CssBaseline } from '@mui/material';
import * as Sentry from '@sentry/react';
import { wrapUseRoutes } from '@sentry/react';
import { useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import router from './router/router';
import { getAbility } from './auth/utils/defineAbilityFor';
import { sentryConfig } from './config/sentry';
import { AbilityContext } from './context/canContext';
import DialogProvider from './context/dialogContext';
import { TaskWatchProvider } from './context/TaskWatchContext';
import { useAppSelector } from './hooks/hooks';
import { useAbilityRoleBootstrap } from './hooks/useAbilityRoleBootstrap';
import { useSelectedApplicationGroupId } from './hooks/useSelectedApplicationGroupId';
import { useSelectedApplicationId } from './hooks/useSelectedApplicationId';
import { useUrlContextValidation } from './hooks/useUrlContextValidation';
import Status500 from './pages/Fallbacks/Status/Status500/Status500';
import { ThemeProviderWrapper } from './features/appTheme';
import {
  APP_CONTEXT_ROUTES,
  GROUP_CONTEXT_ROUTES,
} from './constants/routing';
import { Helmet } from 'react-helmet-async';
import {
  DynamicBrandProvider,
  useDynamicBrand,
} from './context/DynamicBrandContext';
import BrandSettingsDrawer from './features/dynamicBrand/BrandSettingsDrawer';
import React, { useEffect, useMemo, useRef } from 'react';
import { useAdminUrlNavigation } from './hooks/useAdminUrlNavigation';
import {
  getHomeDashboardRedirectPath,
  getSelectionSyncRedirectPath,
} from './router/dashboardRedirect';

const isLegacyDetailPath = (pathname: string) => {
  const [, rootPath, childPath] = pathname.split('/');

  return (
    childPath !== undefined &&
    childPath !== '' &&
    childPath !== 'create' &&
    (APP_CONTEXT_ROUTES.includes(rootPath) ||
      GROUP_CONTEXT_ROUTES.includes(rootPath))
  );
};

// Disables sentry completely
if (process.env.NODE_ENV !== 'development') {
  Sentry.init(sentryConfig);
}

const useSentryRoutes = wrapUseRoutes(useRoutes);

function App() {
  const content = useSentryRoutes(router);
  const navigate = useNavigate();
  const location = useLocation();

  const abilityRole = useAppSelector((state) => state.abilityRole);
  const { selectedApplicationId } = useSelectedApplicationId();
  const { selectedGroupId } = useSelectedApplicationGroupId();
  const {
    isUrlNavigationEnabled,
    neutralDashboardPath,
  } = useAdminUrlNavigation();
  const ability = useMemo(
    () =>
      abilityRole.abilityRoles?.length > 0
        ? getAbility(abilityRole.abilityRoles)
        : getAbility([]),
    [abilityRole.abilityRoles]
  );

  const isInitialMount = useRef(true);
  const initialPathnameRef = useRef(location.pathname);
  const skippedInitialLegacyDetailSyncRef = useRef(false);
  const pathnameRef = useRef(location.pathname);

  useAbilityRoleBootstrap(isUrlNavigationEnabled);
  useUrlContextValidation(isUrlNavigationEnabled);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const dashboardRedirectPath = getHomeDashboardRedirectPath({
      isUrlNavigationEnabled,
      pathname: location.pathname,
      selectedGroupId,
      selectedApplicationId,
      neutralDashboardPath,
    });

    if (dashboardRedirectPath) {
      navigate(dashboardRedirectPath, { replace: true });
    }
  }, [
    isUrlNavigationEnabled,
    location.pathname,
    navigate,
    neutralDashboardPath,
    selectedApplicationId,
    selectedGroupId,
  ]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    /*
      IF CHANGING TO ALL APPLICATIONS THEN GO TO DASHBOARD
      ELSE GO TO ROOT OF THE PAGE YOU ARE CURRENTLY ON

      Note: intentionally not including navigate as a
      dependency so we don't trigger this effect every
      time we navigate.
    */
    const pathname = pathnameRef.current;

    if (
      !skippedInitialLegacyDetailSyncRef.current &&
      pathname === initialPathnameRef.current &&
      isLegacyDetailPath(pathname)
    ) {
      skippedInitialLegacyDetailSyncRef.current = true;
      return;
    }

    const selectionSyncRedirectPath = getSelectionSyncRedirectPath({
      isUrlNavigationEnabled,
      pathname,
      selectedGroupId,
      selectedApplicationId,
      neutralDashboardPath,
    });

    if (selectionSyncRedirectPath) {
      navigate(selectionSyncRedirectPath);
    }
  }, [
    isUrlNavigationEnabled,
    neutralDashboardPath,
    selectedApplicationId,
    selectedGroupId,
  ]);

  return (
    <DynamicBrandProvider>
      <AppContent content={content} ability={ability} />
    </DynamicBrandProvider>
  );
}

function AppContent({
  content,
  ability,
}: {
  content: React.ReactNode;
  ability: ReturnType<typeof getAbility>;
}) {
  const { config } = useDynamicBrand();
  const brandName = config.brandName;
  const faviconHref = config.faviconUrl || config.logoUrl;

  useEffect(() => {
    const href = faviconHref || '/static/images/brand/switch/favicon.png';
    const existing =
      document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (existing) existing.remove();

    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = href;
    document.head.appendChild(link);
  }, [faviconHref]);

  return (
    <ThemeProviderWrapper>
      <Helmet>
        <title>{brandName ? `${brandName} Console` : 'Console'}</title>
      </Helmet>
      <Sentry.ErrorBoundary
        fallback={
          <Status500 resetErrorBoundary={() => window.location.reload()} />
        }
      >
        <AbilityContext.Provider value={ability}>
          <CssBaseline />
          <DialogProvider>
            <ToastContainer theme={'dark'} />
            <TaskWatchProvider>{content}</TaskWatchProvider>
            <BrandSettingsDrawer />
          </DialogProvider>
        </AbilityContext.Provider>
      </Sentry.ErrorBoundary>
    </ThemeProviderWrapper>
  );
}

export default App;
